export type ProSubjectType = 'EMPLOYEE' | 'CANDIDATE' | 'COMPANY'
export type ProCaseStatus = 'OPEN' | 'IN_PROGRESS' | 'BLOCKED' | 'SUBMITTED' | 'COMPLETED' | 'CANCELLED'
export type ProCaseSource = 'MANUAL' | 'RENEWAL' | 'ONBOARDING'
export type ProStepStatus = 'PENDING' | 'IN_PROGRESS' | 'DONE' | 'SKIPPED' | 'BLOCKED'

/**
 * The board columns, in the order work actually moves.
 *
 * COMPLETED and CANCELLED are deliberately last and deliberately present: a board that hides
 * finished work makes it impossible to see what was done this week.
 */
export const PRO_BOARD_COLUMNS: ProCaseStatus[] = [
  'OPEN', 'IN_PROGRESS', 'BLOCKED', 'SUBMITTED', 'COMPLETED', 'CANCELLED',
]

export const PRO_STATUS_LABEL: Record<ProCaseStatus, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In progress',
  BLOCKED: 'Blocked',
  SUBMITTED: 'With the authority',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
}

export const PRO_STATUS_TONE = {
  OPEN: 'info',
  IN_PROGRESS: 'primary',
  BLOCKED: 'danger',
  SUBMITTED: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
} as const

export const PRO_SOURCE_LABEL: Record<ProCaseSource, string> = {
  MANUAL: 'Opened by hand',
  RENEWAL: 'Opened automatically before expiry',
  ONBOARDING: 'Opened by onboarding',
}

export interface ProTemplateView {
  id: string
  legalEntityId: string | null
  code: string
  name: string
  description: string | null
  category: string | null
  subjectType: ProSubjectType
  producesDocumentTypeId: string | null
  /** Days before expiry to open a renewal case; null means somebody starts it by hand. */
  renewalLeadDays: number | null
  active: boolean
  seeded: boolean
  sortOrder: number
}

export interface ProStepView {
  id: string
  seq: number
  name: string
  description: string | null
  durationDays: number | null
  responsibleRoleId: string | null
  /** Null on every seeded step: fees vary by emirate, free zone and year. */
  typicalFee: number | null
  feeCurrency: string | null
  producesDocumentTypeId: string | null
  requiresChecklistComplete: boolean
}

export interface ProChecklistRuleView {
  id: string
  nationality: string | null
  designationId: string | null
  gradeId: string | null
  establishmentId: string | null
  documentTypeId: string | null
  label: string | null
  mandatory: boolean
  seq: number
  active: boolean
}

export interface ProCaseView {
  id: string
  caseNo: string | null
  templateId: string
  templateName: string | null
  subjectType: ProSubjectType
  employeeId: string | null
  employeeName: string | null
  candidateRef: string | null
  legalEntityId: string
  establishmentId: string | null
  status: ProCaseStatus
  currentStepSeq: number | null
  assignedToUserId: string | null
  openedOn: string
  targetDate: string | null
  completedAt: string | null
  source: ProCaseSource
  overdue: boolean
  notes: string | null
}

export interface ProCaseStepView {
  id: string
  seq: number
  name: string
  status: ProStepStatus
  expectedDays: number | null
  startedAt: string | null
  completedAt: string | null
  actualDays: number | null
  referenceNo: string | null
  notes: string | null
}

export interface ProChecklistItemView {
  id: string
  documentTypeId: string | null
  label: string
  mandatory: boolean
  seq: number
  satisfied: boolean
  satisfiedAt: string | null
  storedFileId: string | null
  /** Set when the upload also became a document on the person's own record. */
  employeeDocumentId: string | null
  notes: string | null
}

export interface ProCaseCostView {
  id: string
  caseStepId: string | null
  description: string
  amount: number
  currency: string
  incurredOn: string
  costCategory: string | null
  referenceNo: string | null
  receiptFileId: string | null
}

export interface ProCaseDecisionContext {
  proCase: ProCaseView
  steps: ProCaseStepView[]
  checklist: ProChecklistItemView[]
  /** Mandatory items still to gather — non-empty means approval will be refused. */
  outstandingChecklist: string[]
  costs: ProCaseCostView[]
  /** Null when the case has costs in more than one currency; they are never summed. */
  costSoFar: number | null
  costCurrency: string | null
}

// --- reports ---

export interface ProStatusCount {
  status: string
  cases: number
  overdue: number
}

export interface ProStepDuration {
  stepName: string
  completedCount: number
  expectedDays: number | null
  /** Null while no case has finished the step — zero would read as "instant". */
  averageActualDays: number | null
  stillRunning: number
}

export interface ProCostSummary {
  legalEntityId: string | null
  currency: string
  cases: number
  total: number
  averagePerCase: number
}
